import { sendDraftFollowupEmails } from "@/lib/email/leadEmails.functions";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { attributionColumns } from "@/lib/attribution";

const SESSION_KEY = "mfi_draft_session";
const DRAFT_KEY = "mfi_draft_state";
const FOLLOWUP_KEY = "mfi_draft_followup_sent";
const IDLE_MS = 2 * 60 * 1000; // 2 minutes
const SAVE_DEBOUNCE_MS = 600;
const HIDDEN_GRACE_MS = 8000; // ignore quick tab-switches under 8s
const EXIT_INTENT_COOLDOWN_MS = 4000; // don't re-arm exit intent within 4s of activity
const MOBILE_BOUNCE_MIN_DY = 90; // px of upward travel needed to count as bounce
const MOBILE_BOUNCE_MIN_VELOCITY = 1.4; // px/ms — fast flick, not a slow scroll

interface DraftState {
  id?: string;
  session_id: string;
  name?: string;
  phone?: string;
  email?: string;
  zip?: string;
  linear_feet?: number;
  source?: string;
  page_path?: string;
  last_field?: string;
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function loadLocalDraft(): Partial<DraftState> {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<DraftState>;
  } catch {
    return {};
  }
}

function saveLocalDraft(d: Partial<DraftState>) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* ignore */
  }
}

function followupAlreadySent(): boolean {
  try {
    return sessionStorage.getItem(FOLLOWUP_KEY) === "1";
  } catch {
    return false;
  }
}

function markFollowupSent() {
  try {
    sessionStorage.setItem(FOLLOWUP_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Field name → draft column. Looks at autoComplete first, then name/id. */
function classifyField(el: HTMLInputElement | HTMLTextAreaElement): keyof DraftState | null {
  const ac = (el.autocomplete || "").toLowerCase();
  const nm = `${el.name || ""} ${el.id || ""}`.toLowerCase();

  if (ac === "name" || /(^|[-_])name([-_]|$)/.test(nm)) return "name";
  if (ac === "tel" || el.type === "tel" || /phone|mobile|tel/.test(nm)) return "phone";
  if (ac === "email" || el.type === "email" || /email/.test(nm)) return "email";
  if (ac === "postal-code" || /\bzip\b|postal/.test(nm)) return "zip";
  if (/\b(feet|linear[-_]?feet|footage|length)\b/.test(nm)) return "linear_feet";
  return null;
}

function readFormSource(form: HTMLFormElement): string | undefined {
  return (
    form.dataset['captureSource'] ||
    form.id ||
    form.getAttribute("aria-label") ||
    undefined
  );
}

/** Detect if we have enough info to actually be useful to follow up on. */
function isFollowupReady(d: Partial<DraftState>): boolean {
  if (!d.name || d.name.trim().length < 2) return false;
  return Boolean((d.phone && d.phone.replace(/\D/g, "").length >= 7) || (d.email && /.+@.+\..+/.test(d.email)));
}

let installed = false;

/**
 * Global form-draft capture: watches every <form data-capture-draft> on the
 * page for input/blur events on Name, Phone, Email, Zip, and Linear-feet
 * fields. Persists to sessionStorage and a server-side `lead_drafts` row.
 *
 * Triggers a "finish your quote" follow-up when:
 *   - the user shows exit intent (mouse leaves viewport via top, or
 *     mobile rapid scroll-up), OR
 *   - 2 minutes pass with no input activity after they've started a draft.
 *
 * Only fires once per session, only if at least name + (phone OR email) are
 * captured, and only if the form was never actually submitted (we mark the
 * draft converted on form submit).
 */
export function installFormDraftCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const sessionId = getSessionId();
  const local = loadLocalDraft();
  const draft: DraftState = {
    session_id: sessionId,
    page_path: window.location.pathname,
    ...local,
  };

  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let triggered = false;

  const persistRemote = async () => {
    if (!isFollowupReady(draft)) return;
    try {
      if (!draft.id) {
        const id = crypto.randomUUID();
        const { error } = await supabase.from("lead_drafts").insert({
        ...attributionColumns(),
          id,
          session_id: draft.session_id,
          name: draft.name ?? null,
          phone: draft.phone ?? null,
          email: draft.email ?? null,
          zip: draft.zip ?? null,
          linear_feet: draft.linear_feet ?? null,
          source: draft.source ?? null,
          page_path: draft.page_path ?? window.location.pathname,
          status: "open",
        });
        if (!error) {
          draft.id = id;
          saveLocalDraft(draft);
        }
      } else {
        await supabase
          .from("lead_drafts")
          .update({
            name: draft.name ?? null,
            phone: draft.phone ?? null,
            email: draft.email ?? null,
            zip: draft.zip ?? null,
            linear_feet: draft.linear_feet ?? null,
            source: draft.source ?? null,
            page_path: draft.page_path ?? window.location.pathname,
          })
          .eq("id", draft.id)
          .eq("status", "open");
      }
    } catch {
      /* offline / blocked — local copy is enough */
    }
  };

  const scheduleSave = () => {
    // Local copy is written immediately on every keystroke so refresh / nav
    // never loses progress; remote upsert is debounced to limit network chatter.
    saveLocalDraft(draft);
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      void persistRemote();
    }, SAVE_DEBOUNCE_MS);
  };

  const fireFollowup = async (trigger: "exit-intent" | "idle-timeout") => {
    if (triggered || followupAlreadySent()) return;
    if (!isFollowupReady(draft)) return;
    triggered = true;
    markFollowupSent();

    // Surface in UI for an in-page "we saved your progress" prompt
    window.dispatchEvent(
      new CustomEvent("draft-followup-fired", {
        detail: { draft: { ...draft }, trigger },
      }),
    );

    try {
      await sendDraftFollowupEmails({
        data: {
          draftId: draft.id ?? "",
          trigger,
          name: draft.name,
          email: draft.email ?? undefined,
          phone: draft.phone,
          zip: draft.zip,
          linearFeet: draft.linear_feet,
          source: draft.source,
          pagePath: draft.page_path,
          resumeUrl: `${window.location.origin}${draft.page_path || "/contact"}?resume=1`,
        },
      });

      if (draft.id) {
        await supabase
          .from("lead_drafts")
          .update({
            status: "follow_up_sent",
            follow_up_kind: trigger,
            last_followup_sent_at: new Date().toISOString(),
          })
          .eq("id", draft.id);
      }

      void import("@/lib/analytics").then(({ trackEvent }) =>
        trackEvent("draft_followup_sent", {
          trigger,
          has_email: Boolean(draft.email),
          source: draft.source,
        }),
      );
    } catch (err) {
      console.warn("draft followup failed", err);
    }
  };

  // Track recent user activity so we don't fire exit-intent during normal use.
  let lastActivityAt = 0;
  const markActivity = () => {
    lastActivityAt = Date.now();
  };

  const isFieldFocused = () => {
    const ae = document.activeElement as HTMLElement | null;
    if (!ae) return false;
    const tag = ae.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (ae.isContentEditable) return true;
    return false;
  };

  const recentlyActive = () => Date.now() - lastActivityAt < EXIT_INTENT_COOLDOWN_MS;

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // Don't pull the trigger while the user is mid-typing or otherwise active.
      if (isFieldFocused() || recentlyActive()) {
        resetIdleTimer();
        return;
      }
      void fireFollowup("idle-timeout");
    }, IDLE_MS);
  };

  const handleInput = (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return;
    const form = target.closest("form");
    if (!form || !form.hasAttribute("data-capture-draft")) return;

    const field = classifyField(target);
    if (!field) return;

    const value = target.value.trim();
    if (field === "linear_feet") {
      const n = parseInt(value, 10);
      if (Number.isFinite(n) && n > 0) draft.linear_feet = n; else delete draft.linear_feet;
    } else {
      (draft as unknown as Record<string, unknown>)[field] = value;
    }
    draft.last_field = field;
    const source = readFormSource(form); if (source) draft.source = source; else delete draft.source;
    const pp = window.location.pathname; if (pp) draft.page_path = pp; else delete draft.page_path;

    markActivity();
    scheduleSave();
    resetIdleTimer();
  };

  const handleSubmit = (e: Event) => {
    const form = e.target as HTMLFormElement | null;
    if (!form || !form.hasAttribute("data-capture-draft")) return;
    // Mark draft converted so we don't follow up after a real submission.
    markFollowupSent();
    const convertedDraftId = draft.id;
    if (convertedDraftId) {
      void supabase
        .from("lead_drafts")
        .update({ status: "converted" })
        .eq("id", convertedDraftId);
    }
    if (idleTimer) clearTimeout(idleTimer);
    // Surface a "Discard draft" prompt so users can scrub their info from
    // this device after a successful submission. Snapshot the draft + id
    // first because we'll likely clear local state if they confirm.
    window.dispatchEvent(
      new CustomEvent("draft-submitted", {
        detail: {
          draft: { ...draft },
          draftId: convertedDraftId,
          source: readFormSource(form),
        },
      }),
    );
  };

  const handleMouseOut = (e: MouseEvent) => {
    // Desktop only — mobile fires bogus mouseout events
    if (window.innerWidth < 768) return;
    // Suppress while typing or just after activity (e.g. moving cursor to nav)
    if (isFieldFocused() || recentlyActive()) return;
    // Pointer leaving toward the top (typical "going to close tab" gesture)
    if (e.clientY <= 0 && !e.relatedTarget) {
      void fireFollowup("exit-intent");
    }
  };

  // Mobile bounce detection — require a fast upward flick, not a casual scroll
  let lastY = window.scrollY;
  let lastScrollAt = performance.now();
  const handleScroll = () => {
    const now = performance.now();
    const y = window.scrollY;
    markActivity();
    if (window.innerWidth >= 768) {
      lastY = y;
      lastScrollAt = now;
      return;
    }
    const dy = lastY - y; // positive = scrolling up
    const dt = Math.max(1, now - lastScrollAt);
    const velocity = dy / dt; // px per ms
    if (
      y < 120 &&
      dy > MOBILE_BOUNCE_MIN_DY &&
      velocity > MOBILE_BOUNCE_MIN_VELOCITY &&
      !isFieldFocused()
    ) {
      void fireFollowup("exit-intent");
    }
    lastY = y;
    lastScrollAt = now;
  };

  // Quick tab-switches (cmd-tab, glance at notification) should NOT trigger.
  // Only fire if the tab stays hidden for >= HIDDEN_GRACE_MS or actually closes.
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  const handleVisibility = () => {
    if (document.visibilityState === "hidden") {
      if (isFieldFocused()) return; // don't punish someone using a password manager popup
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(() => {
        // Re-check — they may have come back
        if (document.visibilityState === "hidden") {
          void fireFollowup("exit-intent");
        }
      }, HIDDEN_GRACE_MS);
    } else {
      // Came back quickly — cancel the pending fire
      if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
      markActivity();
    }
  };

  // Pagehide / beforeunload = real exit, fire immediately (no grace period)
  const handlePageHide = () => {
    if (hiddenTimer) clearTimeout(hiddenTimer);
    void fireFollowup("exit-intent");
  };

  document.addEventListener("input", handleInput, true);
  document.addEventListener("submit", handleSubmit, true);
  document.addEventListener("mouseout", handleMouseOut);
  document.addEventListener("visibilitychange", handleVisibility);
  document.addEventListener("keydown", markActivity, true);
  document.addEventListener("pointerdown", markActivity, true);
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("pagehide", handlePageHide);

  // If we already have a viable draft from a previous nav within this tab,
  // start the idle clock immediately.
  if (isFollowupReady(draft)) resetIdleTimer();
}

/** Read-only access to the draft for UI components (e.g., exit-intent modal). */
export function readDraft(): Partial<DraftState> {
  return loadLocalDraft();
}

/**
 * Wipe the locally-cached draft + (optionally) the server-side row. Called
 * from the post-submit "Discard draft" prompt so users can scrub their info
 * from a shared device.
 */
export async function clearDraft(draftId?: string) {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
  if (draftId) {
    try {
      await supabase
        .from("lead_drafts")
        .update({
          status: "converted",
          name: null,
          phone: null,
          email: null,
          zip: null,
          linear_feet: null,
        })
        .eq("id", draftId);
    } catch {
      /* best-effort */
    }
  }
  window.dispatchEvent(new CustomEvent("draft-discarded"));
}

/** React hook for components that want to live-render the draft. */
export function useFormDraftCapture() {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    installFormDraftCapture();
  }, []);
}
